import { storage } from '@uhg-abyss/web/tools/storage';

import { Constants } from '../../../../common/Constants';
import { i18n } from '../../../../common/Utils/i18n/i18n';
import { DefaultLanguage, LANGUAGES, LanguageObject, Locale } from './types';

export const getLanguageFromCookie = () => {
  if (typeof document !== 'undefined') {
    const langPref = storage.cookie.get(Constants.LANGPREF);
    if (langPref) {
      return langPref;
    }
    // In the integrated experience, there are two cookies for PS_Locale, one with domain 'uhc.com' and the other with domain 'member.int.uhc.com'. Only the 'member.int.uhc.com' cookie is being updated when the language is changed, so we need to check both cookies and return the correct one.
    const cookieValues = document.cookie
      .split('; ')
      .filter((row) => row.startsWith(Constants.PSLOCALE))
      .map((row) => row.split('=')[1]);
    const initialLocale = storage.session.get(
      Constants.STORAGE_KEYS.SESSION.INITIAL_LOCALE
    );
    if (cookieValues.length > 1 && cookieValues[0] !== cookieValues[1]) {
      return cookieValues[0] === initialLocale
        ? cookieValues[1]
        : cookieValues[0];
    }
    return storage.cookie.get(Constants.PSLOCALE);
  }
  return undefined;
};

export const getLobFromCookie = () => {
  if (typeof document !== 'undefined') {
    return storage.cookie.get(Constants.LOBTARGET)?.split('-')[0];
  }
  return undefined;
};

export const getSupportedLanguagesFromSessionStorage = (): string[] => {
  if (typeof document !== 'undefined') {
    const supportedLanguages = storage.session.get(
      Constants.STORAGE_KEYS.SESSION.SUPPORTED_LANGUAGES
    );
    if (supportedLanguages) {
      return JSON.parse(supportedLanguages);
    }
  }
  return [Locale.enUS, Locale.esUS];
};

export const getActualLanguageCode = (
  lang: Locale,
  supportedLanguages?: string[]
): string => {
  if (!supportedLanguages) {
    supportedLanguages = getSupportedLanguagesFromSessionStorage();
  }
  if (lang === Locale.enUS || !supportedLanguages.includes(lang)) {
    return DefaultLanguage.code;
  }
  switch (lang) {
    case Locale.esUS:
      return LANGUAGES.es.code;
    case Locale.myMM:
      return LANGUAGES.my.code;
    case Locale.htHT:
      return LANGUAGES.ht.code;
    case Locale.zhTW:
      return LANGUAGES.zh.code;
    case Locale.viVN:
      return LANGUAGES.vi.code;
    case Locale.ilPH:
      return LANGUAGES.ic.code;
    case Locale.koUS:
      return LANGUAGES.ko.code;
    default:
      return DefaultLanguage.code;
  }
};

export const getLanguage = (
  langCode?: string,
  supportedLanguages?: string[]
): LanguageObject => {
  let lang: Locale = langCode
    ? langCode
    : getLanguageFromCookie() || Locale.enUS;
  if (supportedLanguages === undefined) {
    supportedLanguages = getSupportedLanguagesFromSessionStorage();
  }
  if (supportedLanguages.includes(lang) === false) {
    lang = Locale.enUS;
  }

  // To change app language to Spanish for local development, uncomment
  //    the next line and comment out the other instance of `const lang`
  // const lang = getLanguageFromCookie() || Locale.esUS;

  const actualLanguageCode = getActualLanguageCode(lang, supportedLanguages);
  return LANGUAGES[actualLanguageCode];
};

export const setLanguage = (lang: LanguageObject) => {
  const currentLanguage = getLanguage();
  if (lang.locale !== currentLanguage.locale) {
    if (lang.locale === Locale.esUS) {
      i18n.changeLanguage(LANGUAGES.es.locale);
    } else if (lang.locale === Locale.myMM) {
      i18n.changeLanguage(LANGUAGES.my.locale);
    } else if (lang.locale === Locale.htHT) {
      i18n.changeLanguage(LANGUAGES.ht.locale);
    } else if (lang.locale === Locale.zhTW) {
      i18n.changeLanguage(LANGUAGES.zh.locale);
    } else if (lang.locale === Locale.viVN) {
      i18n.changeLanguage(LANGUAGES.vi.locale);
    } else if (lang.locale === Locale.ilPH) {
      i18n.changeLanguage(LANGUAGES.ic.locale);
    } else if (lang.locale === Locale.koUS) {
      i18n.changeLanguage(LANGUAGES.ko.locale);
    } else {
      i18n.changeLanguage(LANGUAGES.en.locale);
    }
  }

  return new Promise<LanguageObject>((resolve, reject) => {
    if (lang.locale !== currentLanguage.locale) {
      if (typeof document !== 'undefined') {
        storage.cookie.set(Constants.PSLOCALE, lang.locale);
        resolve(lang);
      }

      reject('An error occurred when attempting to set the language');
    } else {
      reject(lang);
    }
  });
};

export const initializeLanguage = (portalData?, urlLocale?): LanguageObject => {
  const locale = urlLocale ? urlLocale : portalData?.portalConfig?.language;
  return getLanguage(locale);
};
